import { createContext, useState, useCallback, useMemo } from 'react';

export const ToastContext = createContext(null);

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Add toast
  const addToast = useCallback((message, options = {}) => {
    const id = ++toastId;
    const toast = {
      id,
      message,
      type: options.type || 'info',
      duration: options.duration || 5000
    };

    setToasts((prev) => [...prev, toast]);

    // Auto remove
    if (toast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, toast.duration);
    }

    return id;
  }, []);

  // Remove toast
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Helper methods
  const toast = useMemo(() => ({
    success: (message, options) => addToast(message, { ...options, type: 'success' }),
    error: (message, options) => addToast(message, { ...options, type: 'error' }),
    warning: (message, options) => addToast(message, { ...options, type: 'warning' }),
    info: (message, options) => addToast(message, { ...options, type: 'info' })
  }), [addToast]);

  const value = useMemo(() => ({
    toasts,
    addToast,
    removeToast,
    toast
  }), [toasts, addToast, removeToast, toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast Container */}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <span>{t.message}</span>
            <button onClick={() => removeToast(t.id)} className="toast-close">×</button>
          </div>
        ))}
      </div>
      <style>{`
        .toast-container {
          position: fixed;
          top: var(--spacing-4);
          right: var(--spacing-4);
          z-index: 10000;
          display: flex;
          flex-direction: column;
          gap: var(--spacing-2);
          max-width: 400px;
        }
        .toast {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--spacing-3) var(--spacing-4);
          border-radius: var(--radius-lg);
          color: var(--color-white);
          font-weight: 500;
          box-shadow: var(--shadow-lg);
          animation: slideDown var(--transition-slow);
        }
        .toast-success { background-color: var(--color-success); }
        .toast-error { background-color: var(--color-error); }
        .toast-warning { background-color: var(--color-warning); }
        .toast-info { background-color: var(--color-info); }
        .toast-close {
          background: none;
          border: none;
          color: inherit;
          font-size: var(--text-xl);
          cursor: pointer;
          margin-left: var(--spacing-3);
          opacity: 0.8;
        }
        .toast-close:hover { opacity: 1; }
      `}</style>
    </ToastContext.Provider>
  );
};

